import { 
  encounterTables, 
  biomeEncounterChance, 
  revisitEncounterMultiplier,
  poiEncounterTables,
  poiEncounterChance,
  environmentalEncounterTable,
  environmentalEncounterChance
} from '../data/encounterTables';
import { encounterTemplates } from '../data/encounters';
import { createLogger } from './logger';

const logger = createLogger('encounter-generator');

/**
 * Determines the effective biome for encounter purposes.
 * Maps tile properties to encounter table keys.
 */
const getEncounterBiome = (tile) => {
  if (!tile) return 'plains';

  // Towns get their own encounter table
  if (tile.poi === 'town' || tile.poi === 'city' || tile.poi === 'village' || tile.poi === 'hamlet') {
    return 'town';
  }

  // Map poi types to biomes for encounter purposes
  if (tile.poi === 'forest') return 'forest';
  if (tile.poi === 'mountain') return 'mountain';

  // Check tile biome property
  if (tile.biome === 'water') return 'water';
  if (tile.biome === 'beach') return 'beach';
  if (tile.biome === 'forest') return 'forest';
  if (tile.biome === 'mountain') return 'mountain';
  if (tile.biome === 'plains') return 'plains';
  if (tile.biome === 'snow') return 'snow';
  if (tile.biome === 'desert') return 'desert';

  // Default fallback
  return 'plains';
};

/**
 * Weighted random selection from an encounter table.
 * Returns the selected entry.
 */
const weightedRandom = (table) => {
  const totalWeight = table.reduce((sum, entry) => sum + entry.weight, 0);
  let random = Math.random() * totalWeight;

  for (const entry of table) {
    random -= entry.weight;
    if (random <= 0) return entry;
  }

  return table[table.length - 1];
};

// Tiles the party only PASSES THROUGH during auto-travel (#84) roll the biome and
// environmental encounters at this fraction of the normal chance; the destination tile
// keeps full odds. The base rates were tuned for one click per tile, and a multi-tile
// journey made fights feel near-constant. Tunable: see src/game/travelOdds.js for the
// per-journey numbers each value gives.
export const PASS_THROUGH_ENCOUNTER_MULTIPLIER = 0.5;

// World-map road tiles (hasPath) roll biome and environmental encounters at this
// fraction of the normal chance. Stacks with the pass-through multiplier.
export const ROAD_ENCOUNTER_MULTIPLIER = 0.5;

/**
 * The chance (0..0.70) that the biome encounter roll fires on this tile. Pure: shared by
 * shouldTriggerEncounter and the journey-odds calculator (travelOdds.js).
 */
export const biomeTriggerChance = (tile, isFirstVisit, settings, movesSinceLastEncounter = 0) => {
  const biome = getEncounterBiome(tile);

  // No encounters on water
  if (biome === 'water') return 0;

  // Get base chance for this biome
  let chance = biomeEncounterChance[biome] || 0.25;

  // Reduce chance on revisited tiles
  if (!isFirstVisit) {
    const multiplier = revisitEncounterMultiplier[biome] || 0.3;
    chance *= multiplier;
  }

  // Grimness modifier: darker campaigns have more encounters
  const grimnessModifier = {
    'Noble': 0.8,
    'Gritty': 1.0,
    'Dark': 1.2,
    'Grimdark': 1.4
  };
  chance *= grimnessModifier[settings?.grimnessLevel] || 1.0;

  // Increase chance slightly if it's been a while since last encounter
  // (prevents long stretches of nothing happening).
  // Towns are exempt: the pity/streak bonus is an absolute add that dwarfs the
  // tiny town base and, combined with per-tile rolling during a town walk, made
  // town crossings trigger fights far too often. Keep towns at a flat base rate
  // regardless of the shared moves counter. World-map and site (cave/ruins)
  // biomes keep the pity bonus unchanged.
  const isTown = biome === 'town';
  if (!isTown && movesSinceLastEncounter >= 3) {
    chance += 0.10;
  }
  if (!isTown && movesSinceLastEncounter >= 5) {
    chance += 0.15;
  }

  // Cap at 70% — always some chance of peaceful travel
  return Math.min(chance, 0.70);
};

/**
 * Check if an encounter should happen on this tile.
 *
 * @param {Object} tile - The map tile the player moved to
 * @param {boolean} isFirstVisit - Whether this is the first time visiting this tile
 * @param {Object} settings - Game settings (for grimness modifier)
 * @param {number} movesSinceLastEncounter - Moves since last encounter occurred
 * @param {number} [chanceMultiplier=1] - Scales the chance (pass-through travel tiles)
 * @returns {boolean} Whether an encounter should trigger
 */
export const shouldTriggerEncounter = (tile, isFirstVisit, settings, movesSinceLastEncounter = 0, chanceMultiplier = 1) => {
  const biome = getEncounterBiome(tile);
  const isTown = biome === 'town';
  const baseChance = biomeEncounterChance[biome] || 0.25;
  const chance = biomeTriggerChance(tile, isFirstVisit, settings, movesSinceLastEncounter) * chanceMultiplier;

  const roll = Math.random();
  logger.debug('[ENCOUNTER DEBUG] shouldTriggerEncounter calc:', {
    baseChance,
    isFirstVisit,
    revisitMultiplier: !isFirstVisit ? (revisitEncounterMultiplier[biome] || 0.3) : 1,
    grimness: settings?.grimnessLevel,
    moveBonus: isTown ? 0 : (movesSinceLastEncounter >= 5 ? 0.25 : (movesSinceLastEncounter >= 3 ? 0.10 : 0)),
    finalChance: chance,
    roll,
    triggered: roll < chance
  });

  return roll < chance;
};

/**
 * Get the POI type for a tile if applicable.
 * Returns null if the tile doesn't have a POI with special encounters.
 */
const getPoiType = (tile) => {
  if (!tile || !tile.poi) return null;

  // World maps store cave POIs as 'cave_entrance'; the encounter tables key on 'cave'.
  // Without this mapping, cave-themed world encounters never fired at cave tiles.
  if (tile.poi === 'cave_entrance') return 'cave';

  const poiTypes = ['cave', 'ruins', 'grove', 'forest', 'mountain', 'peak'];
  return poiTypes.includes(tile.poi) ? tile.poi : null;
};

/** True when the tile has its own POI encounter roll (cave, ruins, grove, forest, mountain, peak). */
export const hasPoiEncounter = (tile) => getPoiType(tile) !== null;

/**
 * Roll a random encounter for a given tile.
 * Returns an encounter template or null if 'none' is rolled.
 * 
 * @param {Object} tile - The map tile
 * @param {Object} settings - Game settings
 * @returns {Object|null} The encounter object or null
 */
export const rollRandomEncounter = (tile, settings) => {
  const biome = getEncounterBiome(tile);
  const poiType = getPoiType(tile);
  
  // Use POI-specific table if available, otherwise biome table
  let table;
  if (poiType && poiEncounterTables[poiType]) {
    table = poiEncounterTables[poiType];
  } else {
    table = encounterTables[biome] || encounterTables['plains'];
  }

  const roll = weightedRandom(table);

  if (roll.template === 'none') return null;

  const template = encounterTemplates[roll.template];
  if (!template) {
    logger.warn(`[ENCOUNTER] Template not found: ${roll.template}`);
    return null;
  }

  return {
    ...template,
    templateKey: roll.template,
    isHostile: roll.hostile !== false,
    // The template's authored encounterTier is the source of truth (P3): each encounter
    // declares 'immediate' | 'narrative' and that decides blocking-modal vs Look-around
    // hook. The hostile-derived value is only a fallback for old data missing the field.
    encounterTier: template.encounterTier || (roll.hostile !== false ? 'immediate' : 'narrative'),
    sourceBiome: biome,
    sourcePoiType: poiType
  };
};

/**
 * Roll for an environmental encounter.
 * These can occur in any biome based on weather/hazard conditions.
 * 
 * @param {Object} tile - The map tile
 * @param {Object} settings - Game settings
 * @param {Object} [options] - Roll context
 * @param {boolean} [options.enclosedInterior] - True when rolling inside an enclosed
 *   site interior (cave, mountain), which suppresses open-air `setting: 'outdoor'`
 *   weather/sky hazards. Defaults to false so open-air and world-map rolls are unchanged.
 * @returns {Object|null} The environmental encounter or null
 */
/** The chance that the environmental roll fires on this tile (before its 'none' entries). */
export const environmentalTriggerChance = (tile, settings) => {
  const biome = getEncounterBiome(tile);
  const chance = environmentalEncounterChance[biome] || 0.10;
  // Apply grimness modifier
  const grimnessModifier = {
    'Noble': 0.7,
    'Gritty': 1.0,
    'Dark': 1.3,
    'Grimdark': 1.5
  };
  return chance * (grimnessModifier[settings?.grimnessLevel] || 1.0);
};

export const rollEnvironmentalEncounter = (tile, settings, options = {}) => {
  const { enclosedInterior = false, chanceMultiplier = 1 } = options;
  const biome = getEncounterBiome(tile);
  const adjustedChance = environmentalTriggerChance(tile, settings) * chanceMultiplier;

  if (Math.random() > adjustedChance) return null;

  // Determine the current climate from the campaign theme (primary) then the
  // tile biome, so climate-inappropriate hazards can be suppressed.
  //   snow -> cold, desert -> hot, everything else -> temperate.
  let climate = 'temperate';
  if (settings?.theme === 'snow' || biome === 'snow') climate = 'cold';
  else if (settings?.theme === 'desert' || biome === 'desert') climate = 'hot';

  // Filter to climate-compatible entries. Untagged templates and those tagged
  // 'any' are ALWAYS eligible (backwards-compatible with older/untagged saves);
  // a tagged template only survives if its climate matches the current climate.
  const climateTable = environmentalEncounterTable.filter((entry) => {
    if (entry.template === 'none') return true;
    const template = encounterTemplates[entry.template];
    const tag = template?.climate;
    if (!tag || tag === 'any') return true;
    return tag === climate;
  });

  // Inside an enclosed interior (cave / mountain), suppress open-air weather/sky
  // hazards. Untagged templates and those tagged 'any' are always eligible
  // (backwards-compatible); a template tagged 'outdoor' is dropped underground.
  // If the filter empties the table, roll nothing rather than erroring.
  const settingTable = enclosedInterior
    ? climateTable.filter((entry) => {
        if (entry.template === 'none') return true;
        const template = encounterTemplates[entry.template];
        return template?.setting !== 'outdoor';
      })
    : climateTable;

  if (settingTable.length === 0) return null;

  const roll = weightedRandom(settingTable);

  if (roll.template === 'none') return null;
  
  const template = encounterTemplates[roll.template];
  if (!template) {
    logger.warn(`[ENCOUNTER] Environmental template not found: ${roll.template}`);
    return null;
  }
  
  return {
    ...template,
    templateKey: roll.template,
    isHostile: false,
    isEnvironmental: true,
    // Honor the authored tier (P3): storms/earthquakes are 'immediate' (blocking) but
    // fog/strange-lights are authored 'narrative' and should be Look-around hooks on the
    // world map, not forced modals. Fallback keeps old behavior for data missing the field.
    encounterTier: template.encounterTier || 'immediate',
    sourceBiome: biome
  };
};

/**
 * Check specifically for POI encounters.
 * Higher chance than regular encounters when at a POI.
 * 
 * @param {Object} tile - The map tile
 * @param {boolean} isFirstVisit - First time visiting
 * @param {Object} settings - Game settings
 * @returns {Object|null} The POI encounter or null
 */
export const checkForPoiEncounter = (tile, isFirstVisit, settings) => {
  const poiType = getPoiType(tile);
  if (!poiType) return null;
  
  let chance = poiEncounterChance[poiType] || 0.35;
  
  // Lower chance on revisits
  if (!isFirstVisit) {
    chance *= 0.4;
  }
  
  // Grimness modifier
  const grimnessModifier = {
    'Noble': 0.8,
    'Gritty': 1.0,
    'Dark': 1.15,
    'Grimdark': 1.3
  };
  chance *= grimnessModifier[settings?.grimnessLevel] || 1.0;
  
  if (Math.random() > chance) return null;
  
  const table = poiEncounterTables[poiType];
  const roll = weightedRandom(table);
  
  if (roll.template === 'none') return null;
  
  const template = encounterTemplates[roll.template];
  if (!template) {
    logger.warn(`[ENCOUNTER] POI template not found: ${roll.template}`);
    return null;
  }
  
  return {
    ...template,
    templateKey: roll.template,
    isHostile: roll.hostile !== false,
    // Authored tier is source of truth (P3); hostile-derived value is the fallback.
    encounterTier: template.encounterTier || (roll.hostile !== false ? 'immediate' : 'narrative'),
    sourcePoiType: poiType
  };
};

/**
 * Per-STEP wandering roll for the INSIDE of a site (cave/ruins corridors), drawn from the
 * site type's POI encounter table.
 *
 * This is deliberately NOT checkForPoiEncounter: that function models a ONE-TIME ARRIVAL at
 * a world-map POI (~50% "caves almost always have something"), which is correct when you
 * step onto a cave tile ONCE, but catastrophic when re-rolled on EVERY tile of a multi-tile
 * cave walk — a 50% flat per-step chance with no pity throttle floods the corridor with
 * combats (playtest 2026-07-18: cave_bats popped almost every step). Here the base chance is
 * low and the shared pity timer applies, so a cave crawl produces the occasional wandering
 * foe, not a wall of them. Encounters stay theme-correct (same POI table) and honor their
 * authored encounterTier. Environmental/weather hazards are intentionally NOT rolled inside
 * sites (enclosed interiors have no sky); the caller handles those it wants.
 *
 * @param {string} siteType - 'cave' | 'ruins' | ... (a poiEncounterTables key)
 * @param {Object} settings - game settings (grimness modifier)
 * @param {number} movesSinceLastEncounter - steps since the last site encounter fired
 * @returns {Object|null} an encounter template (with encounterTier) or null
 */
// Per-step base chance. Tuned between the old flood (the 50% arrival roll re-fired every
// step) and empty (playtest 2026-07-18: a full cave traverse with zero spawns at 0.12).
// Note only ~1/3 of fired rolls are immediate-tier combat (the rest are narrative/none,
// which are dropped), so the effective per-step FIGHT rate is well under this number. This
// is the TOP-UP layer over the ambient roamers seeded at population/re-entry (which supply
// the site's baseline visible life), so it can stay modest.
export const SITE_WANDER_BASE_CHANCE = 0.24;

const DIFFICULTY_ORDER = ['trivial', 'easy', 'medium', 'hard', 'deadly'];

/**
 * Hardest difficulty a party of `level` should meet from a RANDOM site wandering roll.
 * This is a TOP CAP only, deliberately different from sitePopulator's difficultyBandForLevel
 * (which ALSO drops easy foes for high parties, right for a placed slot boss): a wandering
 * roll should still throw the occasional easy hazard (a bat swarm, a cave-in) at any level,
 * it just must never blindside a low party with a hard nest or a deadly guardian they never
 * chose to fight (playtest 2026-07-18). Placed mobs are already level-matched; this closes
 * the same gap on the wandering path. Unknown level (legacy callers / tests) → no cap.
 */
export const maxWanderDifficultyForLevel = (level) => {
  if (!Number.isFinite(level)) return 'deadly';
  const L = Math.max(1, Math.floor(level));
  if (L <= 2) return 'medium'; // low parties: no hard/deadly
  if (L <= 5) return 'hard';   // mid parties: no deadly
  return 'deadly';             // veterans: the guardian is fair game
};

export const rollSiteWanderingEncounter = (siteType, settings, movesSinceLastEncounter = 0, partyLevel = null) => {
  const table = poiEncounterTables[siteType];
  if (!table) return null;

  let chance = SITE_WANDER_BASE_CHANCE;
  const grimnessModifier = { Noble: 0.8, Gritty: 1.0, Dark: 1.2, Grimdark: 1.4 };
  chance *= grimnessModifier[settings?.grimnessLevel] || 1.0;
  // Pity bonus so a quiet stretch reliably yields something (the pity timer only resets on
  // an immediate hit, so it keeps climbing through narrative/none rolls).
  if (movesSinceLastEncounter >= 3) chance += 0.07;
  if (movesSinceLastEncounter >= 5) chance += 0.11;
  chance = Math.min(chance, 0.42);

  if (Math.random() > chance) return null;

  // Level-gate the pool (top cap): drop any encounter harder than the party's cap so a
  // random roll never blindsides a low party. Runs AFTER the fire roll, so the gate only
  // changes WHAT can appear, never HOW OFTEN. 'none' and any untagged/unknown-difficulty
  // entry always stay (never over-block), so a fully-capped pool still yields a quiet step.
  const capIdx = DIFFICULTY_ORDER.indexOf(maxWanderDifficultyForLevel(partyLevel));
  const eligible = table.filter((entry) => {
    if (entry.template === 'none') return true;
    const di = DIFFICULTY_ORDER.indexOf(encounterTemplates[entry.template]?.difficulty);
    return di < 0 || di <= capIdx;
  });

  const roll = weightedRandom(eligible.length ? eligible : table);
  if (roll.template === 'none') return null;
  const template = encounterTemplates[roll.template];
  if (!template) {
    logger.warn(`[ENCOUNTER] Site wandering template not found: ${roll.template}`);
    return null;
  }
  return {
    ...template,
    templateKey: roll.template,
    isHostile: roll.hostile !== false,
    encounterTier: template.encounterTier || (roll.hostile !== false ? 'immediate' : 'narrative'),
    sourcePoiType: siteType
  };
};

/**
 * Convenience function: check + roll in one call.
 * Now includes POI and environmental encounter checks.
 * Priority: POI encounters > Environmental > Regular biome encounters
 * 
 * @param {Object} tile - The map tile
 * @param {boolean} isFirstVisit - First time visiting this tile
 * @param {Object} settings - Game settings
 * @param {number} movesSinceLastEncounter - Moves since last encounter
 * @param {Object} [options] - Roll context passed through to the environmental roll
 * @param {boolean} [options.enclosedInterior] - True for enclosed site interiors
 *   (caves, mountains); suppresses open-air `setting: 'outdoor'` environmental hazards.
 *   Defaults to false so every existing caller is unaffected.
 * @returns {Object|null} The encounter or null
 */
export const checkForEncounter = (tile, isFirstVisit, settings, movesSinceLastEncounter = 0, options = {}) => {
  const biome = getEncounterBiome(tile);
  logger.debug('[ENCOUNTER DEBUG] checkForEncounter called:', {
    tile: { biome: tile?.biome, poi: tile?.poi },
    effectiveBiome: biome,
    isFirstVisit,
    movesSinceLastEncounter,
    baseChance: biomeEncounterChance[biome]
  });
  
  // First check for POI-specific encounters (higher priority)
  const poiEncounter = checkForPoiEncounter(tile, isFirstVisit, settings);
  if (poiEncounter) {
    logger.debug('[ENCOUNTER] POI encounter triggered:', poiEncounter.name);
    return poiEncounter;
  }
  
  // Then check for environmental encounters
  const envEncounter = rollEnvironmentalEncounter(tile, settings, options);
  if (envEncounter) {
    logger.debug('[ENCOUNTER] Environmental encounter triggered:', envEncounter.name);
    return envEncounter;
  }
  
  // Fall back to regular biome encounters
  const willTrigger = shouldTriggerEncounter(tile, isFirstVisit, settings, movesSinceLastEncounter, options.chanceMultiplier ?? 1);
  logger.debug('[ENCOUNTER DEBUG] shouldTriggerEncounter result:', willTrigger);
  
  if (!willTrigger) {
    return null;
  }

  const encounter = rollRandomEncounter(tile, settings);
  if (encounter) {
    logger.debug('[ENCOUNTER] Biome encounter triggered:', encounter.name);
  } else {
    logger.debug('[ENCOUNTER DEBUG] rollRandomEncounter returned null (rolled "none")');
  }
  return encounter;
};
